import { __decorate, __metadata } from "tslib";
import { Serialize } from '@ftr/serialization';
import { BigNumber } from 'bignumber.js';
import { CurrencyUtils } from './Currency';
import { CurrenciesNotCompatible } from './error';
/**
 * A value object indicating a value of a certain currency.
 * Uses a BigNumber amount, which serializes as a string, so do not use this type in contracts or messages.
 */
export class Money {
    constructor(amount, currency) {
        if (amount || amount === 0) {
            // Deserializing may cause this to construct without args
            this.amount = new BigNumber(amount);
        }
        this.currency = currency;
        this.symbol = CurrencyUtils.getCurrencySymbol(this.currency);
    }
    static ensureSameCurrency(...moneys) {
        ensureSameCurrency(moneys.map(m => m.currency));
    }
    static max(...moneys) {
        if (moneys.length === 0) {
            throw new Error('Money.max operation called with no arguments');
        }
        Money.ensureSameCurrency(...moneys);
        const currency = moneys[0].currency;
        return new Money(BigNumber.max(...moneys.map(m => m.amount)), currency);
    }
    static sum(...moneys) {
        if (moneys.length === 0) {
            throw new Error('Money.sum operation called with no arguments');
        }
        Money.ensureSameCurrency(...moneys);
        return moneys.reduce((sum, money) => sum.plus(money));
    }
    equals(other) {
        return this.currency === other.currency && this.amount.isEqualTo(other.amount);
    }
    plus(other) {
        Money.ensureSameCurrency(this, other);
        return new Money(this.amount.plus(other.amount), this.currency);
    }
    minus(other) {
        Money.ensureSameCurrency(this, other);
        return new Money(this.amount.minus(other.amount), this.currency);
    }
    multipliedBy(factor) {
        return new Money(this.amount.multipliedBy(factor), this.currency);
    }
    format(format = 'short', decimalPlaces = 2) {
        switch (format) {
            case 'long':
                return `${this.currency.toString().toUpperCase()} ${this.symbol || '$'}${this.amount.toFormat(decimalPlaces)}`;
            case 'short':
                return `${this.symbol || '$'}${this.amount.toFormat(decimalPlaces)}`;
            default:
                return 'unknown format';
        }
    }
}
__decorate([
    Serialize(),
    __metadata("design:type", BigNumber // BigNumber used to avoid floating point precision issues
    /**
     * The currency of this price's <code>value</code>.
     */
    )
], Money.prototype, "amount", void 0);
function ensureSameCurrency(currencies) {
    const distinctCurrencies = new Set(currencies);
    if (distinctCurrencies.size > 1) {
        throw new CurrenciesNotCompatible(...Array.from(distinctCurrencies));
    }
}
